/**
 * This module describes application states returned by the "update" event
 * @version 0.3
 * @exports src/app_state
 * @namespace Application
 */

import {shadingType, selectionType, scanMode, objectType} from "_src/constants";
import { cameraType, MAX_UINT32_ELEMS } from "_src/engine/constants";
import events from "_src/engine/events";
import output from "_src/engine/output";

/**
 * Object or Group information. Includes most of the fields described [here]{@link https://confluence.artec-group.com:4432/display/WS/Project+JSON+structure}.
 * Additional fields are as follows.
 * @typedef {Object} ObjectInfo
 * @memberof Application
 * @property {MT_SCAN|MT_FUSION|MT_GROUP} type - Type of the object
 * @property {Array} frames - List of frames. Frames have {@link Application.FrameInfo} type. For objects only.
 * @property {Number} numPolygons - Number of polygons. For objects only.
 * @property {Number} numVertices - Number of vertices. For objects only.
 * @property {Number} maxError - The highest error value in scan's frames. For objects only.
 * @property {String} groupID - Id of this object's group.
 */

 /**
 * @typedef {Object} FrameInfo
 * @memberof Application
 * @property {string} name - Frame's name
 * @property {Array(3)} color - Color of the frame
 * @property {Boolean} textured - Whether the frame has a texture
 * @property {Number} error - The error value of the frame
 * @property {Number} numPolygons - Number of polygons in this frame
 * @property {Number} numVertices - Number of vertices in this frame
 */

/**
 * Application state generated by the "update" event
 * @memberof Application
 * @typedef {Object} State
 * @property {Number} FPS - Current frames per second
 * @property {Boolean} isApplyingHistoryStep - Informs if undo/redo is being applied
 * @property {Boolean} isSaving - Informs if the project is being saved at the moment
 * @property {Boolean} isLoading - Informs if the project is being loading at the moment
 * @property {Boolean} hasLoadingErrors - Informs that project is not fully loaded, http errors have happened
 * @property {Boolean} updatingStates - Informs if the project has objects with being updated state
 * @property {Boolean} editingAllowed - Whether or not application allows any objects' editing
 * @property {Boolean} bboxVisibility - Is bbox visible
 * @property {Boolean} showGrid - Is grid plane visible
 * @property {Boolean} showGridAxes - Are grid axes visible
 * @property {BM_DIFFUSE|BM_TRANSPARENT|BM_FRONTFACE|BM_NONE} backFaceMode - Current backface mode
 * @property {Boolean} lighting - Is lighting enabled
 * @property {Boolean} selectThrough - Is select through enabled
 * @property {Boolean} hasSelectedGeometry - Is any geometry on the scene selected
 * @property {Number} selectionSphereRadius - Radius of the selection sphere
 * @property {Number} selectionCircleRadius - Radius of the selection circle
 * @property {Number} normalsLength - Length of normals
 * @property {Number} xrayEdgeFalloff - Xray edge falloff
 * @property {Number} xrayIntensity - Xray Intensity
 * @property {Number} lightingAngle - Horizontal angle of scene lighting
 * @property {TT_NONE|TT_VIEW|TT_ALIGN|TT_ERASER|TT_SMOOTHER|TT_TRANSFORM|TT_LOD|TT_POSITIONER|TT_MEASUREMENTS|TT_CADFITTER|TT_DEFEATURE|TT_TEXTURE_HEALER||TT_CADPICKER} activeTool - Currently active tool
 * @property {ST_SPHERE|ST_CIRCLE|ST_RECTANGLE|ST_LASSO|ST_NONE} selectionType - Type of selection
 * @property {SM_SELECT|SM_UNSELECT} selectionMode - Selection mode
 * @property {CT_ORTHO|CT_PERSPECTIVE} cameraType - Type of camera
 * @property {CC_TRACKBALL|CC_ORBIT|CC_PHOTO} cameraControl - Type of camera control
 * @property {RM_SOLID|RM_WIREFRAME|RM_POINTS} renderMode - Render mode
 * @property {CM_TEXTURE|CM_SCAN_COLOR|CM_FRAME_COLOR|CM_MAX_ERROR|CM_XRAY} colorMode - Color mode
 * @property {SHT_FLAT|SHT_SMOOTH} shadingType - Type of shading
 * @property {{r, g, b}} wireframeColor - Wireframe color
 * @property {{r, g, b}} backFaceColor - Color of back faces of the scene objects
 * @property {string} hoverOn - ID of the object currently hovered by mouse
 * @property {Number} positionerPointsCount - Number of points currently selected in Positioner manipulator
 * @property {Number} memoryUsage - Total memory size in bytes allocated by geometry on CPU side
 * @property {{leftAlignPoints: Array(), rightAlignPoints: Array()}} alignPoints - Arrays of align Points in screen coordinates
 * @property {Number} hoverWindowOn - ID of the window currently hovered by mouse
 * @property {Number} smoothStrength - Strength of the smooth brush
 * @property {Boolean} showAllFrames - Whether to show all frames or only textured frames
 * @property {Boolean} showNormals - Are object's normals visible
 * @property {Boolean} showViewcube - Is viewcube visible
 * @property {Boolean} enableCameraControls - Are camera controls enabled
 * @property {Boolean} manipulatorIsActive - Is manipulator active
 * @property {Boolean} annotationsEnabled - Whether the annotation mode are enabled
 * @property {SM_NONE|SM_CONCURRENT|SM_BLEND} surfelsFilteringMode - Surfels filtering mode
 * @property {Number} surfelsNoiseSize - Size of surlfes noised points
 * @property {Boolean} GPUTimeDebugEnabled - Whether the GPU frame time debug enabled
 * @property {Boolean} distanceMapMode - Does any distance map displayed
 * @property {Number} measurementType - Type of measurement creating in measurements manipulator
 * @property {Array(Number)|null} photoViewRectangleInternal - Screen coordinates of photo view rectangle or null if photo mode is disabled (for internal usage)
 * @property {Array(Number)} photoFitArea - Screen coordinates of photo fitting area used for photoViewRectangle
 * @property {scanMode.TRIANGLES|scanMode.SURFELS_HIGH|scanMode.SURFELS_MID} scanMode - Scan visualization method
 */

const CT_ORTHO       = cameraType.ORTHO;
const CT_PERSPECTIVE = cameraType.PERSPECTIVE;

const CC_TRACKBALL  = 0;
const CC_ORBIT      = 1;
const CC_PHOTO      = 2;

const ST_NONE          = selectionType.NONE;
const ST_SPHERE        = selectionType.SPHERE;
const ST_CIRCLE        = selectionType.CIRCLE;
const ST_RECTANGLE     = selectionType.RECTANGLE;
const ST_CUT_OFF_PLANE = selectionType.CUT_OFF_PLANE;
const ST_LASSO         = selectionType.LASSO;

const SM_SELECT    = 0;
const SM_UNSELECT  = 1;

const SHT_SMOOTH = shadingType.SMOOTH;
const SHT_FLAT   = shadingType.FLAT;

const TT_NONE           = -1;
const TT_VIEW           =  0;
const TT_ALIGN          =  1;
const TT_ERASER         =  2;
const TT_SMOOTHER       =  3;
const TT_TRANSFORM      =  4;
const TT_LOD            =  5;
const TT_POSITIONER     =  6;
const TT_DEFEATURE      =  7;
const TT_TEXTURE_HEALER =  8;
const TT_MEASUREMENTS   =  9;
const TT_CADFITTER      = 10;
const TT_CADPICKER      = 11;

const RM_SOLID           = 0;
const RM_WIREFRAME       = 1;
const RM_POINTS          = 2;

const CM_TEXTURE         = 0;
const CM_SCAN_COLOR      = 1;
const CM_FRAME_COLOR     = 2;
const CM_MAX_ERROR       = 3;
const CM_XRAY            = 4;

const SM_NONE            = 0;
const SM_CONCURRENT      = 1;
const SM_BLEND           = 2;

const GS_OCTREE      = 0b00000001;
const GS_HALF_EDGE   = 0b00000010;
const GS_MINMAX      = 0b00000100;
const GS_DRAW_DEPTH  = 0b00001000;
const GS_NORMALS     = 0b00010000;

const BM_NONE         = 0;
const BM_DIFFUSE      = 1;
const BM_TRANSPARENT  = 2;
const BM_FRONTFACE    = 3;

const stateValues = new Map();
const specificHandlers = new Map();
specificHandlers.set("isApplyingHistoryStep", setIsApplyingHistoryStep);
specificHandlers.set("isSaving", setIsSaving);
specificHandlers.set("isLoading", setIsLoading);
specificHandlers.set("hasLoadingErrors", setHasLoadingErrors);
specificHandlers.set("updatingStates", setStateUpdateRequired);
specificHandlers.set("hoverOn", setHoverOn);
specificHandlers.set("hoverWindowOn", setHoverWindowOn);
specificHandlers.set("cachingObjectsNumber", addUnloadingObjectsNumber);

const noRedrawStates = new Set();
noRedrawStates.add("FPS");
noRedrawStates.add("hoverOn");
noRedrawStates.add("alignPoints");
noRedrawStates.add("hoverWindowOn");
noRedrawStates.add("manipulatorIsActive");
noRedrawStates.add("memoryUsage");
noRedrawStates.add("selectionCircleRadius");

const defaultState = {
  "activeTool": TT_NONE,
  "selectionType":  ST_NONE,
  "selectionMode":  SM_SELECT,
  "selectionSphereRadius":  100,
  "selectionCircleRadius":  100,
  "normalsLength": 25,
  "cameraType":  CT_PERSPECTIVE,
  "cameraControl": CC_TRACKBALL,
  "renderMode":  RM_SOLID,
  "colorMode":  CM_TEXTURE,
  "bboxVisibility":  false,
  "shadingType":  SHT_FLAT,
  "xrayEdgeFalloff":  0.1,
  "xrayIntensity":  1.1,
  "backFaceMode": BM_DIFFUSE,
  "lighting":  true,
  "lightingAngle":  0.0,
  "hasSelectedGeometry":  false,
  "selectThrough":  true,
  "backFaceColor":  {"r": 58 / 255, "g": 58 / 255, "b": 61 / 255}, // #3A3B3D
  "wireframeColor":  {"r": 0.0, "g": 0.0, "b": 0.0},
  "isApplyingHistoryStep":  false,
  "isSaving":  false,
  "isLoading": false,
  "hasLoadingErrors": false,
  "updatingStates":  false,
  "editingAllowed":  true,
  "FPS": 0,
  "hoverOn": null,
  "positionerPointsCount": 0,
  "memoryUsage": 0,
  "alignPoints": null,
  "hoverWindowOn": null,
  "smoothStrength": 0.03,
  "softwareRenderingWidth": 1,
  "softwareRenderingHeight": 1,
  "cachingObjectsNumber" : 0,
  "showAllFrames": false,
  "showGrid": true,
  "showGridAxes": true,
  "showNormals": false,
  "showViewcube": true,
  "enableCameraControls": true,
  "manipulatorIsActive": false,
  "annotationsEnabled":  false,
  "surfelsFilteringMode": SM_CONCURRENT,
  "surfelsNoiseSize": 1.0,
  "GPUTimeDebugEnabled": false,
  "cadFitterPickingMode": true,
  "distanceMapMode": false,
  "measurementType": objectType.LINEAR_MEASURE,
  "photoViewRectangleInternal": null,
  "photoFitArea": null,
  "scanMode": scanMode.SURFELS_HIGH,
};

function clean() {
  for (let key in defaultState) {
    stateValues.set(key, null);
  }
}

function setAndEmit(stateName, stateValue) {
  // NOTE: this check only works for primitive types (e.g. color will be set anyway)
  const currentValue = getStateValue(stateName);
  if (stateValue === Object(stateValue) || currentValue !== stateValue) {
    stateValues.set(stateName, stateValue);
    events.emit("update", {[stateName]: stateValue});
  }
}

function setStateValue(stateName, stateValue) {

  if (!noRedrawStates.has(stateName)) {
    events.emit("frame_requested");
  }

  if (specificHandlers.has(stateName)) {
    specificHandlers.get(stateName)(stateValue);
  } else if (stateValues.has(stateName)) {
    setAndEmit(stateName, stateValue);
  } else {
    output.error(output.DEVELOPERS_ERROR, `unknown app state: ${stateName}.`);
  }
}

function getStateValue(stateName) {
  if (!stateValues.has(stateName)) {
    output.error(output.DEVELOPERS_ERROR, `unknown app state: ${stateName}.`);
  }

  return stateValues.get(stateName);
}

clean();

export default {

  // camera types
  CT_ORTHO,
  CT_PERSPECTIVE,

  // camera control types
  CC_TRACKBALL,
  CC_ORBIT,
  CC_PHOTO,

  // rendering modes
  RM_SOLID,
  RM_WIREFRAME,
  RM_POINTS,

  // color modes
  CM_TEXTURE,
  CM_SCAN_COLOR,
  CM_FRAME_COLOR,
  CM_MAX_ERROR,
  CM_XRAY,

  // surfels filtering modes
  SM_NONE,
  SM_CONCURRENT,
  SM_BLEND,

  // selection type
  ST_SPHERE,
  ST_CIRCLE,
  ST_RECTANGLE,
  ST_LASSO,
  ST_CUT_OFF_PLANE,
  ST_NONE,

  //selection mode
  SM_SELECT,
  SM_UNSELECT,

  // shading type
  SHT_SMOOTH,
  SHT_FLAT,

  // tool type
  TT_NONE,
  TT_VIEW,
  TT_ALIGN,
  TT_ERASER,
  TT_SMOOTHER,
  TT_TRANSFORM,
  TT_LOD,
  TT_POSITIONER,
  TT_MEASUREMENTS,
  TT_CADFITTER,
  TT_CADPICKER,
  TT_DEFEATURE,
  TT_TEXTURE_HEALER,

  stateFlags : 0,
  GS_OCTREE,
  GS_HALF_EDGE,
  GS_MINMAX,
  GS_DRAW_DEPTH,
  GS_NORMALS,

  // backface mode
  BM_NONE,
  BM_DIFFUSE,
  BM_TRANSPARENT,
  BM_FRONTFACE,

  init() {
    for (let key in defaultState) {
      setStateValue(key, defaultState[key]);
    }
  },

  extractStates() {
    return this.stateFlags;
  },

  syncStates(states) {
    this.stateFlags = states;
  },

  setState(mask, value) {
    this.stateFlags = value ? this.stateFlags | mask : this.stateFlags & (MAX_UINT32_ELEMS ^ mask);
    events.emit("frame_requested");
  },

  getState(mask) {
    return Boolean(this.stateFlags & mask);
  },

  setStateValue,
  getStateValue,

  cleanup() {
    clean();
  }

}

function setIsApplyingHistoryStep(isApplying) {
  setAndEmit("isApplyingHistoryStep", isApplying);
  updateEditingAllowed();
}

function setIsSaving(isSaving) {
  setAndEmit("isSaving", isSaving);
  updateEditingAllowed();
}

function setIsLoading(isLoading) {
  setAndEmit("isLoading", isLoading);
  updateEditingAllowed();
}

function setHasLoadingErrors(hasLoadingErrors) {
  setAndEmit("hasLoadingErrors", hasLoadingErrors);
  updateEditingAllowed();
}

function setStateUpdateRequired(required) {
  setAndEmit("updatingStates", required);
  updateEditingAllowed();
}

function updateEditingAllowed() {
  const editingAllowed = !getStateValue("isApplyingHistoryStep") &&
      !getStateValue("isSaving") &&
      !getStateValue("updatingStates") &&
      !getStateValue("hasLoadingErrors") &&
      !getStateValue("isLoading");
  setAndEmit("editingAllowed", editingAllowed);
}

function setHoverOn(hoverOn) {
  const hoverOff = getStateValue("hoverOn");
  if (hoverOff !== hoverOn) {
    stateValues.set("hoverOn", hoverOn);
    events.emit("update", {"hoverOn": hoverOn, "hoverOff": hoverOff});
  }
}

function setHoverWindowOn(hoverOn) {
  const hoverOff = getStateValue("hoverWindowOn");
  if (hoverOff !== hoverOn) {
    stateValues.set("hoverWindowOn", hoverOn);
    events.emit("update", {"hoverWindowOn": hoverOn, "hoverWindowOff": hoverOff});
  }
}

function addUnloadingObjectsNumber(value) {
  const number = getStateValue("cachingObjectsNumber");
  setAndEmit("cachingObjectsNumber", number + value);
}
